export const controllerStateLabel = (value) =>
  String(value || "unknown").replaceAll("_", " ");

export const controllerStateTone = (value) => {
  if (["ready", "running"].includes(value)) return "isPositive";
  if (["staging", "starting", "quiescing", "stopping", "degraded", "update_required"].includes(value)) {
    return "isWarning";
  }
  if (["faulted", "crash_loop"].includes(value)) return "isDanger";
  return "isNeutral";
};

export const formatControllerBytes = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return "Not reported";
  const units = ["B", "KiB", "MiB", "GiB"];
  let amount = bytes;
  let index = 0;
  while (amount >= 1024 && index < units.length - 1) {
    amount /= 1024;
    index += 1;
  }
  const digits = index === 0 || amount >= 10 ? 0 : 1;
  return `${amount.toFixed(digits)} ${units[index]}`;
};

export const controllerCanEditDraft = (controller) =>
  Boolean(controller) &&
  controller.managed_legacy !== true &&
  controller.mode === "isolated_linux_v1" &&
  controller.desired_state === "stopped";

export const controllerTargetKey = (target) =>
  target ? `${target.kind}\u0000${target.id}` : "";

export const findControllerByPublicName = (controllers, name, targetName = "") => {
  const wantedName = String(name || "").trim().toLocaleLowerCase();
  const wantedTarget = String(targetName || "").trim().toLocaleLowerCase();
  const matches = (Array.isArray(controllers) ? controllers : []).filter((controller) => {
    if (String(controller?.name || "").trim().toLocaleLowerCase() !== wantedName) return false;
    if (!wantedTarget) return true;
    return String(controller?.target?.name || "").trim().toLocaleLowerCase() === wantedTarget;
  });
  return matches.length === 1 ? matches[0] : null;
};

export const PLC_CONTROLLER_LIFECYCLE_ACTIONS = Object.freeze(["start", "stop", "restart", "reset", "retry"]);

const canonicalUUID = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(String(value || ""));

const canonicalSHA256 = (value) => /^[0-9a-f]{64}$/.test(String(value || ""));

export const controllerLifecycleActionAvailability = (
  controller,
  action,
  pageCapability,
  canConfigure = true
) => {
  if (!PLC_CONTROLLER_LIFECYCLE_ACTIONS.includes(action)) {
    return { enabled: false, reason: "This lifecycle action is not supported." };
  }
  if (!canConfigure) {
    return { enabled: false, reason: "Configure permission is required." };
  }
  if (!controller || controller.managed_legacy === true || controller.mode !== "isolated_linux_v1") {
    return { enabled: false, reason: "Lifecycle review applies only to isolated virtual controllers." };
  }
  if (action === "restart" && controller.target?.kind === "edge") {
    return { enabled: false, reason: "Reviewed Restart is unavailable for edge targets." };
  }
  if (
    pageCapability?.executable !== true ||
    controller.capability?.executable !== true ||
    controller.configuration_only === true
  ) {
    return { enabled: false, reason: "Authenticated controller lifecycle is unavailable on this runtime." };
  }
  const backendAction = controller.lifecycle?.actions?.[action];
  if (backendAction?.available !== true) {
    return {
      enabled: false,
      reason: String(backendAction?.message || "This controller action is unavailable on the selected runtime."),
    };
  }
  if (action === "start" || action === "restart") {
    if (action === "start" && controller.desired_state !== "stopped") {
      return { enabled: false, reason: "Start requires stopped desired state." };
    }
    if (
      action === "restart" &&
      (controller.desired_state !== "running" || !Number.isSafeInteger(Number(controller.desired_generation)) || Number(controller.desired_generation) < 1)
    ) {
      return { enabled: false, reason: "Restart requires a running desired generation." };
    }
    if (!Array.isArray(controller.programs) || controller.programs.length === 0) {
      return { enabled: false, reason: `Assign at least one published application before ${action === "start" ? "Start" : "Restart"}.` };
    }
    if (Array.isArray(controller.connectors) && controller.connectors.length > 0) {
      return { enabled: false, reason: `This application-only lifecycle cannot ${action === "start" ? "Start" : "Restart"} controller connector assignments.` };
    }
    if (
      controller.policy?.network_mode !== "none" ||
      !Array.isArray(controller.policy?.network_destinations) || controller.policy.network_destinations.length > 0 ||
      !Array.isArray(controller.policy?.devices) || controller.policy.devices.length > 0
    ) {
      return { enabled: false, reason: `${action === "start" ? "Start" : "Restart"} requires the application-only no-network, no-device policy.` };
    }
    return { enabled: true, reason: "" };
  }
  if (action === "stop") {
    return controller.desired_state === "running" && Number(controller.desired_generation) >= 1
      ? { enabled: true, reason: "" }
      : { enabled: false, reason: "Stop requires a running desired generation." };
  }
  if (action === "reset") {
    const exactCrashLoop =
      controller.desired_state === "running" &&
      Number(controller.desired_generation) >= 1 &&
      controller.actual?.authenticated === true &&
      controller.actual?.stale !== true &&
      controller.actual?.state === "crash_loop";
    return exactCrashLoop
      ? { enabled: true, reason: "" }
      : { enabled: false, reason: "Reset requires fresh authenticated crash-loop evidence for the running generation." };
  }
  return { enabled: true, reason: "" };
};

export const validPLCControllerLifecycleProposal = (payload, controller, requestedAction) => {
  const proposal = payload?.proposal;
  if (
    payload?.schema !== "controlseat.plc.controller-lifecycle-preview" ||
    payload?.version !== 1 ||
    !proposal ||
    proposal.requested_action !== requestedAction ||
    !["start", "stop", "restart", "reset"].includes(proposal.command_action) ||
    (requestedAction !== "retry" && proposal.command_action !== requestedAction) ||
    !canonicalUUID(proposal.confirmation_token) ||
    !canonicalSHA256(proposal.proposal_sha256) ||
    !["local", "edge"].includes(proposal.target_kind) ||
    !["running", "stopped"].includes(proposal.desired_state) ||
    !Number.isSafeInteger(proposal.revision) || proposal.revision < 1 ||
    !Number.isSafeInteger(proposal.generation) || proposal.generation < 0 ||
    !Array.isArray(proposal.applications) ||
    !Array.isArray(proposal.warnings) ||
    proposal.confirmation_required !== true ||
    Number.isNaN(Date.parse(proposal.expires_at))
  ) {
    return false;
  }
  if (proposal.desired_state !== (proposal.command_action === "stop" ? "stopped" : "running")) {
    return false;
  }
  if (
    (proposal.command_action === "restart" && !["preserve", "reset"].includes(proposal.retained_disposition)) ||
    (proposal.command_action !== "restart" && proposal.retained_disposition !== "") ||
    (proposal.command_action === "restart" && !["reject_if_present", "clear"].includes(proposal.force_disposition)) ||
    (proposal.command_action !== "restart" && proposal.force_disposition !== "")
  ) {
    return false;
  }
  if (proposal.applications.some((application) => (
    typeof application?.name !== "string" || !application.name.trim() ||
    !Number.isSafeInteger(application?.published_revision) || application.published_revision < 1
  ))) {
    return false;
  }
  if (proposal.warnings.some((warning) => typeof warning !== "string" || !warning.trim())) {
    return false;
  }
  if (controller) {
    if (proposal.revision !== Number(controller.desired_revision)) return false;
    if (proposal.target_kind !== controller.target?.kind) return false;
    if (requestedAction === "retry") {
      if (
        proposal.generation !== Number(controller.desired_generation) ||
        proposal.desired_state !== controller.desired_state
      ) return false;
    } else {
      if (["start", "restart"].includes(proposal.command_action) && proposal.generation !== Number(controller.desired_generation) + 1) return false;
      if (["stop", "reset"].includes(proposal.command_action) && proposal.generation !== Number(controller.desired_generation)) return false;
    }
  }
  return true;
};

export const plcControllerLifecycleProposalExpired = (proposal, now = Date.now()) => {
  const expires = Date.parse(proposal?.expires_at);
  return Number.isNaN(expires) || expires <= now;
};

export const plcControllerLifecycleCommandMessage = (status) => {
  if (["queued", "leased", "dispatching"].includes(status)) {
    return "The reviewed controller lifecycle command is durably queued.";
  }
  if (status === "succeeded") {
    return "The reviewed controller lifecycle command completed successfully.";
  }
  if (status === "rejected") {
    return "The reviewed controller lifecycle command was rejected.";
  }
  if (status === "ambiguous") {
    return "The reviewed controller lifecycle command requires authenticated state reconciliation.";
  }
  return "";
};

export const validPLCControllerLifecycleConfirmation = (payload, requestedAction, proposal = null) => {
  const command = payload?.command;
  const valid = Boolean(
    payload?.schema === "controlseat.plc.controller-lifecycle-confirmation" &&
    payload?.version === 1 &&
    command &&
    command.requested_action === requestedAction &&
    ["start", "stop", "restart", "reset"].includes(command.command_action) &&
    (requestedAction === "retry" || command.command_action === requestedAction) &&
    command.desired_state === (command.command_action === "stop" ? "stopped" : "running") &&
    (
      (command.command_action === "restart" && ["preserve", "reset"].includes(command.retained_disposition)) ||
      (command.command_action !== "restart" && command.retained_disposition === "")
    ) &&
    (
      (command.command_action === "restart" && ["reject_if_present", "clear"].includes(command.force_disposition)) ||
      (command.command_action !== "restart" && command.force_disposition === "")
    ) &&
    Number.isSafeInteger(command.revision) && command.revision >= 1 &&
    Number.isSafeInteger(command.generation) && command.generation >= 0 &&
    ["queued", "leased", "dispatching", "succeeded", "rejected", "ambiguous"].includes(command.status) &&
    !Number.isNaN(Date.parse(command.queued_at)) &&
    command.message === plcControllerLifecycleCommandMessage(command.status)
  );
  if (!valid || !proposal) return valid;
  return command.command_action === proposal.command_action &&
    command.desired_state === proposal.desired_state &&
    command.revision === proposal.revision &&
    command.generation === proposal.generation &&
    command.retained_disposition === proposal.retained_disposition &&
    command.force_disposition === proposal.force_disposition;
};

const applicationSummary = (applications) => {
  const rows = Array.isArray(applications) ? applications : [];
  return rows.length
    ? rows.map((application) => `${application.name} v${application.published_revision ?? application.published_version}`).join(", ")
    : "None";
};

export const plcControllerLifecycleDiff = (controller, proposal) => [
  { label: "Requested action", before: "No queued action", after: controllerStateLabel(proposal?.requested_action) },
  { label: "Command", before: "No queued command", after: controllerStateLabel(proposal?.command_action) },
  { label: "Desired state", before: controllerStateLabel(controller?.desired_state), after: controllerStateLabel(proposal?.desired_state) },
  { label: "Revision", before: String(controller?.desired_revision ?? "Not reported"), after: String(proposal?.revision ?? "Not reported") },
  { label: "Generation", before: String(controller?.desired_generation ?? "Not reported"), after: String(proposal?.generation ?? "Not reported") },
  { label: "Applications", before: applicationSummary(controller?.programs), after: applicationSummary(proposal?.applications) },
];
